<script setup>
import axios from 'axios';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { extractApiError } from '../apiError';
import {
  formatCompactMoney, formatMoney, formatNumber, formatSignedPercent, signClass, toNumber,
} from '../format';
import { OTHERS_SHARE, dayChange, fitLayout, groupSmall, tileColor } from '../heatmap';
import { intlLocale, t } from '../i18n';
import { readUiState, writeUiState } from '../uiState';
import { PANEL } from '../ui';
import BottomSheet from './BottomSheet.vue';
import HeatmapChart from './HeatmapChart.vue';
import MarketLeaders from './MarketLeaders.vue';
import MarketQuotes from './MarketQuotes.vue';
import MarketSectors from './MarketSectors.vue';
import MarketToday from './MarketToday.vue';
import { canCopyImage, canShareFiles, heatmapPng } from '../heatmapImage';

// «Рынки» tab: heatmap of IMOEX shares or the portfolio's shares (docs/heatmap/SPEC.md).
const props = defineProps({
  // The selected view from portfolioStore (shared with the other screens, decision 10)
  portfolio: {
    type: Object,
    required: true,
  },
  fxRates: {
    type: Object,
    default: () => ({}),
  },
  desktop: {
    type: Boolean,
    default: false,
  },
  // Bumped by pull to refresh: data is read again and the tiles are laid out anew.
  dataVersion: {
    type: Number,
    default: 0,
  },
});

// open-instrument: a tile, a row of «Смотреть все» or a quote opens the instrument screen (docs/instrument/SPEC.md).
const emit = defineEmits(['toast', 'open-instrument']);

const MODES = ['index', 'portfolio'];
const POLL_MS = 5000;
const MOSCOW_DATE = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Moscow' });

// The mode is remembered on the device; the first visit opens the index (decision 11).
const mode = ref(MODES.includes(readUiState('markets.mode', 'index')) ? readUiState('markets.mode', 'index') : 'index');
watch(mode, (value) => writeUiState('markets.mode', value));

const items = ref([]);
// Quotes above the map come with the same answer (docs/quotes/SPEC.md, decision 6).
const quotes = ref([]);
// RVI for «Рынок сегодня» (docs/market-overview/SPEC.md); breadth, leaders and sectors come from `items`.
const rvi = ref(null);
const loading = ref(true);
const error = ref('');
const checkedAt = ref(null);
const nowMs = ref(Date.now());
let pollTimer = null;
let inFlight = false;

const byUid = computed(() => new Map(items.value.map((i) => [i.uid, i])));

async function load() {
  nowMs.value = Date.now();
  if (inFlight) {
    return false;
  }
  inFlight = true;
  try {
    const { data } = await axios.get('/markets/heatmap');
    items.value = data.items ?? [];
    quotes.value = data.quotes ?? [];
    rvi.value = data.rvi ?? null;
    checkedAt.value = Date.now();
    error.value = '';
    return true;
  } catch (e) {
    if (e?.response?.status !== 401) {
      error.value = extractApiError(e, t('heatmap.loadFailed'));
    }
    return false;
  } finally {
    inFlight = false;
    loading.value = false;
    nowMs.value = Date.now();
  }
}

// Shares of the selected view: merged per instrument already; value in roubles at the CBR rate.
const ownShares = computed(() => (props.portfolio.positions ?? [])
  .filter((p) => p.instrument_type === 'share' && p.instrument_uid)
  .map((position) => {
    const item = byUid.value.get(position.instrument_uid);
    const rate = position.currency === 'RUB' || !position.currency ? 1 : toNumber(props.fxRates[position.currency]);
    const price = toNumber(item?.price) ?? toNumber(position.current_price);
    const qty = toNumber(position.quantity) ?? 0;
    return { position, item, value: rate === null || price === null ? null : price * qty * rate };
  }));

// Weights now, by mode (decisions 2, 23): live turnover of index shares, value of own ones; grouped by sector.
function currentEntries() {
  const sectorOf = (uid) => byUid.value.get(uid)?.industry?.sector ?? 'other';
  if (mode.value === 'portfolio') {
    return ownShares.value
      .filter((s) => s.value !== null && s.value > 0)
      .map((s) => ({ key: s.position.instrument_uid, weight: s.value, group: sectorOf(s.position.instrument_uid) }));
  }
  return items.value
    .filter((i) => i.in_index)
    .map((i) => ({ key: i.uid, weight: toNumber(i.turnover) ?? 0, group: sectorOf(i.uid) }));
}

// The layout is fixed until the next open, mode/portfolio change or pull to refresh (decision 9).
// Small shares stay off the map; «Смотреть все» lists every share (decision 26).
const frozen = ref(null);

function relayout() {
  const { tiles, others } = groupSmall(currentEntries(), props.desktop ? OTHERS_SHARE.desktop : OTHERS_SHARE.phone);
  frozen.value = {
    entries: tiles,
    all: [...tiles, ...others].sort((a, b) => b.weight - a.weight).map((e) => e.key),
  };
}

async function reload() {
  if (await load()) {
    relayout();
  }
}

watch([mode, () => props.portfolio.key], relayout);
watch(() => props.dataVersion, reload);

// Width of the map follows the card; height: 4:3 on a phone, 0.6 of the width on a desktop, at most 600 (decisions 19, 31).
const box = ref(null);
const width = ref(0);
let resizeObserver = null;
const height = computed(() => Math.min(Math.round(width.value * (props.desktop ? 0.6 : 0.75)), 600));

// Tiles too small for even a ticker are left off the map (decision 28); they stay in «Смотреть все».
const layout = computed(() => {
  if (!frozen.value || width.value <= 0) {
    return [];
  }
  const entries = frozen.value.entries.map((e) => ({ ...e, ticker: byUid.value.get(e.key)?.ticker ?? '' }));
  return fitLayout(entries, width.value, height.value, props.desktop).rects;
});

const tiles = computed(() => layout.value.map((rect) => {
  const item = byUid.value.get(rect.key);
  const own = ownShares.value.find((s) => s.position.instrument_uid === rect.key);
  const change = dayChange(item?.price, item?.prev_close);
  return {
    ...rect,
    ticker: item?.ticker ?? own?.position.ticker ?? '',
    name: item?.name ?? own?.position.name ?? '',
    change,
    color: tileColor(change),
    hint: hintOf(item, own, change),
  };
}));

function hintOf(item, own, change) {
  const parts = [];
  if (item?.price) {
    parts.push(formatMoney(item.price, item.currency));
  }
  parts.push(formatSignedPercent(change));
  parts.push(sizeText(item, own));
  return parts.filter((p) => p && p !== '—').join(' · ');
}

// Size of one share in words: turnover or the part of the portfolio.
function sizeText(item, own) {
  if (mode.value === 'portfolio') {
    const total = ownTotal.value;
    return own?.value && total ? t('heatmap.sharePart', { value: formatNumber((own.value / total) * 100, 1) }) : '';
  }
  return t('heatmap.turnoverShort', { value: formatCompactMoney(item?.turnover) });
}

const ownTotal = computed(() => ownShares.value.reduce((s, x) => s + (x.value ?? 0), 0));

// Under the title, as in T-Invest (decision 29): when the newest price on the map came, «сегодня» or a date.
const lastTrade = computed(() => {
  const keys = new Set(frozen.value?.all ?? []);
  return latest(items.value.filter((i) => keys.has(i.uid)).map((i) => i.price_time));
});
const status = computed(() => {
  if (!lastTrade.value) {
    return '';
  }
  const day = MOSCOW_DATE.format(new Date(lastTrade.value));
  return day === MOSCOW_DATE.format(new Date(nowMs.value))
    ? t('heatmap.updatedToday', { time: clock(lastTrade.value) })
    : t('heatmap.updatedOn', { date: shortDate(day), time: clock(lastTrade.value) });
});

function latest(times) {
  return times.filter(Boolean).sort((a, b) => Date.parse(a) - Date.parse(b)).at(-1) ?? null;
}

function clock(iso) {
  return new Date(iso).toLocaleTimeString(intlLocale(), { hour: '2-digit', minute: '2-digit' });
}

function shortDate(date) {
  return new Date(`${date}T12:00:00Z`).toLocaleDateString(intlLocale(), { day: '2-digit', month: '2-digit' });
}

const hasEntries = computed(() => (frozen.value?.entries.length ?? 0) > 0);

// «Смотреть все» lists every share of the mode (decision 26); a share opens the instrument screen.
const allOpen = ref(false);

function onSelect(key) {
  emit('open-instrument', key);
}

function openFromAll(key) {
  allOpen.value = false;
  emit('open-instrument', key);
}

const allRows = computed(() => (frozen.value?.all ?? []).map((key) => {
  const item = byUid.value.get(key);
  const own = ownShares.value.find((s) => s.position.instrument_uid === key);
  const change = dayChange(item?.price, item?.prev_close);
  return {
    key,
    ticker: item?.ticker ?? own?.position.ticker ?? '',
    name: item?.name ?? own?.position.name ?? '',
    change,
    size: sizeText(item, own),
  };
}));

const segment = (selected) => (selected ? 'bg-slate-100 text-slate-950' : 'text-slate-300');

function startPolling() {
  if (pollTimer !== null || document.visibilityState !== 'visible') {
    return;
  }
  pollTimer = setInterval(load, POLL_MS);
}

function stopPolling() {
  if (pollTimer !== null) {
    clearInterval(pollTimer);
    pollTimer = null;
  }
}

// Back on the tab: fresh data and a new layout.
function onVisibilityChange() {
  if (document.visibilityState === 'visible') {
    reload();
    startPolling();
  } else {
    stopPolling();
  }
}

onMounted(() => {
  resizeObserver = new ResizeObserver(([entry]) => {
    width.value = Math.floor(entry.contentRect.width);
  });
  watch(box, (el, old) => {
    if (old) {
      resizeObserver.unobserve(old);
    }
    if (el) {
      resizeObserver.observe(el);
    }
  }, { immediate: true });
  document.addEventListener('visibilitychange', onVisibilityChange);
  reload();
  startPolling();
});

// Share (phone) and copy (phone and desktop) the map as a picture (decision 30). The PNG is drawn
// ahead, a moment after the map changes: Safari lets a page share only right after the tap.
const chart = ref(null);
const shareSupported = ref(false);
const copySupported = ref(false);
let png = null;
let pngTimer = null;

function imageName() {
  return `heatmap-${MOSCOW_DATE.format(new Date())}.png`;
}

function renderPng() {
  const svg = chart.value?.svg;
  png = svg ? heatmapPng(svg, { title: t('heatmap.title'), subtitle: status.value }) : null;
  png?.catch(() => {
    png = null;
  });
}

watch([tiles, status, width], () => {
  clearTimeout(pngTimer);
  pngTimer = setTimeout(renderPng, 800);
});

async function share() {
  if (!png) {
    renderPng();
  }
  try {
    const file = new File([await png], imageName(), { type: 'image/png' });
    await navigator.share({ files: [file], title: t('heatmap.title') });
  } catch (e) {
    if (e?.name !== 'AbortError') {
      emit('toast', t('heatmap.shareFailed'), 'info');
    }
  }
}

async function copy() {
  if (!png) {
    renderPng();
  }
  try {
    // A promise in the item keeps the tap's permission in Safari while the PNG is finished.
    await navigator.clipboard.write([new window.ClipboardItem({ 'image/png': png })]);
    emit('toast', t('heatmap.copied'));
  } catch {
    emit('toast', t('heatmap.copyFailed'), 'info');
  }
}

onMounted(() => {
  shareSupported.value = !props.desktop && canShareFiles();
  copySupported.value = canCopyImage();
});

onUnmounted(() => {
  clearTimeout(pngTimer);
  resizeObserver?.disconnect();
  document.removeEventListener('visibilitychange', onVisibilityChange);
  stopPolling();
});
</script>

<template>
  <!-- Market overview (docs/market-overview/SPEC.md): row 1 — quotes and the map, row 2 — «Рынок сегодня»,
       leaders, sectors; on a phone one column: map, quotes, then row 2 (decision 5). -->
  <div class="flex flex-col gap-3 sm:gap-4 lg:gap-6">
    <!-- From lg the quotes stand to the left of the map (docs/quotes/SPEC.md, decision 10); on a phone under it (decision 13). -->
    <div class="flex flex-col gap-3 sm:gap-4 lg:flex-row lg:gap-6">
      <MarketQuotes
        class="max-sm:order-last lg:w-80 lg:shrink-0"
        :quotes="quotes"
        :loading="loading"
        @open="(uid) => emit('open-instrument', uid)"
      />
      <section
        :class="[PANEL, 'lg:min-w-0 lg:flex-1']"
        :aria-busy="loading ? 'true' : undefined"
      >
        <div class="lg:flex lg:items-start lg:justify-between lg:gap-6">
          <div class="min-w-0">
            <h2 class="text-[1.0625rem] font-semibold text-slate-100 sm:text-lg">
              {{ t('heatmap.title') }}
            </h2>
            <p class="mt-0.5 text-[0.8125rem] text-slate-500">
              {{ status }}
            </p>
          </div>

          <div class="lg:w-[360px] lg:shrink-0">
            <div
              class="mt-4 flex rounded-full bg-slate-800 p-0.5 lg:mt-0"
              role="radiogroup"
              :aria-label="t('heatmap.modeAria')"
            >
              <button
                v-for="key in MODES"
                :key="key"
                type="button"
                role="radio"
                :aria-checked="mode === key"
                class="h-8 flex-1 rounded-full text-[0.875rem] font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400"
                :class="segment(mode === key)"
                @click="mode = key"
              >
                {{ t(`heatmap.mode.${key}`) }}
              </button>
            </div>
          </div>
        </div>

        <!-- Phone: the map runs 8 px from the card edge, past its 20 px padding, as in T-Invest (decision 38) -->
        <div
          ref="box"
          class="mt-4 max-sm:-mx-3"
        >
          <!-- Skeleton of the map's height -->
          <div
            v-if="loading"
            class="grid grid-cols-3 grid-rows-3 gap-0.5"
            :style="{ height: `${height}px` }"
          >
            <span class="sr-only">{{ t('common.loading') }}</span>
            <div class="col-span-2 row-span-2 rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="rounded bg-slate-800 motion-safe:animate-pulse" />
            <div class="rounded bg-slate-800 motion-safe:animate-pulse" />
          </div>

          <div
            v-else-if="error && items.length === 0"
            class="flex flex-col items-center justify-center gap-4 text-center"
            :style="{ height: `${height}px` }"
          >
            <p class="text-[0.9375rem] text-rose-300">
              {{ error }}
            </p>
            <button
              type="button"
              class="h-11 rounded-2xl bg-slate-800 px-6 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700"
              @click="reload"
            >
              {{ t('common.retry') }}
            </button>
          </div>

          <div
            v-else-if="!hasEntries"
            class="flex flex-col items-center justify-center gap-2 px-4 text-center"
            :style="{ height: `${Math.min(height, 240)}px` }"
          >
            <template v-if="mode === 'portfolio'">
              <p class="text-[0.9375rem] font-medium text-slate-100">
                {{ t('heatmap.noShares', { name: portfolio.name || portfolio.account_id }) }}
              </p>
              <p class="text-[0.8125rem] text-slate-500">
                {{ t('heatmap.noSharesHint') }}
              </p>
              <button
                type="button"
                class="mt-2 h-11 rounded-2xl bg-slate-800 px-6 text-[0.9375rem] font-medium text-slate-100 active:bg-slate-700"
                @click="mode = 'index'"
              >
                {{ t('heatmap.showIndex') }}
              </button>
            </template>
            <p
              v-else
              class="text-[0.9375rem] text-slate-400"
            >
              {{ t('heatmap.noData') }}
            </p>
          </div>

          <HeatmapChart
            v-else-if="width > 0"
            ref="chart"
            :tiles="tiles"
            :width="width"
            :height="height"
            :desktop="desktop"
            @select="onSelect"
          />
        </div>

        <div
          v-if="!loading && hasEntries"
          class="mt-3 flex items-center justify-end gap-1"
        >
          <button
            type="button"
            class="flex h-11 items-center gap-1 rounded-lg px-2 text-[0.9375rem] text-slate-400 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:text-slate-100"
            aria-haspopup="dialog"
            @click="allOpen = true"
          >
            {{ t('heatmap.showAll') }}
            <span aria-hidden="true">›</span>
          </button>
          <!-- Share on a phone, copy everywhere, bottom right (decision 30); icons only, names for screen readers -->
          <div
            v-if="shareSupported || copySupported"
            class="-mr-2 flex shrink-0"
          >
            <button
              v-if="shareSupported"
              type="button"
              class="flex size-11 items-center justify-center rounded-full text-slate-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800"
              :aria-label="t('heatmap.share')"
              :title="t('heatmap.share')"
              @click="share"
            >
              <svg
                class="size-[22px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M12 3.5v11M8 7l4-3.5L16 7M7.5 10.5H6a1.5 1.5 0 0 0-1.5 1.5v7A1.5 1.5 0 0 0 6 20.5h12a1.5 1.5 0 0 0 1.5-1.5v-7a1.5 1.5 0 0 0-1.5-1.5h-1.5" />
              </svg>
            </button>
            <button
              v-if="copySupported"
              type="button"
              class="flex size-11 items-center justify-center rounded-full text-slate-300 hover:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 active:bg-slate-800"
              :aria-label="t('heatmap.copy')"
              :title="t('heatmap.copy')"
              @click="copy"
            >
              <svg
                class="size-[22px]"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.75"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <rect
                  x="8.5"
                  y="8.5"
                  width="11"
                  height="11"
                  rx="2"
                />
                <path d="M15.5 8.5V6A1.5 1.5 0 0 0 14 4.5H6A1.5 1.5 0 0 0 4.5 6v8A1.5 1.5 0 0 0 6 15.5h2.5" />
              </svg>
            </button>
          </div>
        </div>

        <BottomSheet
          :open="allOpen"
          :title="t('heatmap.allTitle')"
          @close="allOpen = false"
        >
          <ul class="pb-2">
            <li
              v-for="row in allRows"
              :key="row.key"
            >
              <button
                type="button"
                class="flex min-h-12 w-full items-center gap-3 px-5 py-2 text-left text-[0.9375rem] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-slate-400 active:bg-slate-800"
                @click="openFromAll(row.key)"
              >
                <span class="min-w-0 flex-1">
                  <span class="block font-medium text-slate-100">{{ row.ticker }}</span>
                  <span class="block truncate text-[0.8125rem] text-slate-500">{{ row.name }}</span>
                </span>
                <span class="shrink-0 text-right tabular-nums">
                  <span
                    class="block"
                    :class="signClass(row.change)"
                  >{{ formatSignedPercent(row.change) }}</span>
                  <span class="block text-[0.8125rem] text-slate-500">{{ row.size }}</span>
                </span>
              </button>
            </li>
          </ul>
        </BottomSheet>
      </section>
    </div>
    <div class="grid gap-3 sm:gap-4 lg:grid-cols-3 lg:gap-6">
      <MarketToday
        :items="items"
        :rvi="rvi"
        :loading="loading"
      />
      <MarketLeaders
        :items="items"
        :loading="loading"
        @open="(uid) => emit('open-instrument', uid)"
      />
      <MarketSectors
        :items="items"
        :loading="loading"
      />
    </div>
  </div>
</template>
